import { useState, useEffect, useRef } from "react";
import {
  getEmptyFieldsErrorFromTemplate,
  getDefaultFormFromTemplate,
} from "../../utils/form";
import { adminEndpoints } from "../../api/endpoints";
import { useDidMountEffect } from "../../utils/utils";

const getGrowattAccountFormTemplate = (locations, usernameDisabled) => [
  {
    type: "input_chars",
    key: "username",
    name: "Benutzer",
    width: "50%",
    disabled: usernameDisabled,
    default: "",
  },
  {
    type: "input_chars",
    key: "password",
    name: "Passwort",
    width: "50%",
    default: "",
  },
  {
    type: "dropdown",
    key: "location",
    name: "Standort",
    options: (locations || []).map((l) => ({
      label: l.name || `${l.street_and_number} ${l.zip_and_city}`,
      value: l.id,
    })),
    hide: !locations,
    default: null,
  },
];

const defaultGrowattAccountForm = getDefaultFormFromTemplate(
  getGrowattAccountFormTemplate(null, true),
);

export default function useGrowattAccountForm(
  growattAccount,
  location = null,
  resetParent,
  showMsg,
  apiClient,
  isFocused = true,
) {
  const [growattAccountForm, setGrowattAccountForm] = useState({
    ...defaultGrowattAccountForm,
  });
  const [locations, setLocations] = useState(null);
  const [showMissingFields, setShowMissingFields] = useState(false);

  const growattAccountRef = useRef(null);
  const initialized = useRef(false);

  const clearData = () => {
    setGrowattAccountForm(defaultGrowattAccountForm);
    setShowMissingFields(false);
    initialized.current = false;
  };

  const loadData = async () => {
    growattAccountRef.current = null;
    setGrowattAccountForm({
      ...(growattAccount || defaultGrowattAccountForm),
      ...(location ? { location: location.id } : {}),
    });
    if (!location) {
      const response = await apiClient.get(adminEndpoints.location);
      setLocations(response.data);
    } else {
      setLocations([location]);
    }
  };

  const initData = async () => {
    await loadData();
    initialized.current = true;
  };

  useDidMountEffect(() => {
    if (initialized.current) loadData();
  }, [growattAccount]);

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  const submit = async (growattAccountForm) => {
    return growattAccount
      ? apiClient.put(
          adminEndpoints.growattaccount + growattAccount.id,
          growattAccountForm,
        )
      : growattAccountRef.current
        ? apiClient.put(
            adminEndpoints.growattaccount + growattAccountRef.current.id,
            growattAccountForm,
          )
        : apiClient
            .post(adminEndpoints.growattaccount, growattAccountForm)
            .then((res) => {
              growattAccountRef.current = res.data;
              return res;
            });
  };

  const onSubmit = async (onSuccess) => {
    const emptyFieldsError = getEmptyFieldsErrorFromTemplate(
      template,
      growattAccountForm,
    );
    if (emptyFieldsError) {
      setShowMissingFields(true);
      showMsg?.("Bitte alle Informationen eintragen!", "warning");
      console.warn(emptyFieldsError);
      return;
    }

    setShowMissingFields(false);

    try {
      const response = await submit(growattAccountForm);
      resetParent?.(response.data);
      onSuccess?.();
      showMsg?.(
        growattAccount || growattAccountRef.current
          ? "Konto gespeichert."
          : "Konto erfolgreich erstellt.",
      );
    } catch (error) {
      console.warn('Error in "growattAccount:onSubmit"', error, error.stack);
      showMsg?.(
        error?.response?.status === 400
          ? "Dieses Growattkonto konnte nicht gefunden werden."
          : error?.response?.status === 409
            ? "Dieses Growattkonto ist bereits auf dieser Webseite registriert."
            : "Das Growattkonto konnte nicht gespeichert werden.",
        "error",
      );
    }
  };

  const onDelete = async (onSuccess) => {
    setShowMissingFields(false);
    try {
      await apiClient.delete(adminEndpoints.growattaccount);
      resetParent?.();
      onSuccess?.();
      showMsg?.("Konto erfolgreich gelöscht.");
    } catch (error) {
      console.warn('Error in "growattAccount:onDelete"', error, error.stack);
      showMsg?.("Konto konnte nicht gelöscht werden.");
    }
  };

  const template = getGrowattAccountFormTemplate(locations, !!growattAccount);

  return {
    onSubmit,
    form: {
      showMissingFields,
      template,
      form: growattAccountForm,
      setForm: setGrowattAccountForm,
    },
    data: { init: initData, update: loadData, clear: clearData },
    onDelete,
  };
}
